import { useState } from 'react';
import { cn } from '@/shared/cn';
import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  crossAreaFlowRows,
  crossAreaFlowShowDisclosure,
  crossAreaFlowVisibleRows,
} from './CrossAreaFlow.transform';
import { GmCard } from './GmTabs';

/** Status walls stay quiet (cross-route ruling): only the exception takes colour. */
function toneClass(tone: 'bad' | 'warn' | 'ok'): string {
  if (tone === 'bad') return 'text-[var(--state-blocked)]';
  if (tone === 'warn') return 'text-[var(--state-warning)]';
  return 'text-ink-primary';
}

const TH = 'whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-1.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';
const TD = 'border-b border-[var(--color-divider)] px-2 py-[7px] align-middle';

/** map-role-dashboards.md §4a — cross-area flow: upstream divergence,
 * downstream impact, 7-day change (polish.P26 comp: one card, hairline rows). */
export function CrossAreaFlowTable({
  state,
  collapsed = false,
}: {
  state: SessionState;
  /** When true, show the top rows only until expanded (Met KPI tab density). */
  collapsed?: boolean;
}) {
  const rows = crossAreaFlowRows(state);
  const [expanded, setExpanded] = useState(!collapsed);
  const visibleRows = crossAreaFlowVisibleRows(state, expanded, collapsed);
  const showDisclosure = crossAreaFlowShowDisclosure(state, expanded, collapsed);
  return (
    <GmCard title="Cross-area flow — upstream divergence and downstream impact, 7-day change" data-cross-area-flow>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12.5px] tabular-nums">
          <thead>
            <tr>
              <th className={TH}>Process area</th>
              <th className={TH}>Key variable</th>
              <th className={cn(TH, 'text-right')}>7 days ago</th>
              <th className={cn(TH, 'text-right')}>Today</th>
              <th className={cn(TH, 'text-right')}>Change</th>
              <th className={TH}>Downstream impact</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.id} className="[&:last-child>td]:border-b-0" data-cross-area-row={row.id}>
                <td className={cn(TD, 'text-ink-secondary')}>{row.area}</td>
                <td className={cn(TD, 'font-semibold text-ink-primary')}>{row.variable}</td>
                <td className={cn(TD, 'text-right text-ink-secondary')}>{row.sevenDaysAgo}</td>
                <td className={cn(TD, 'text-right font-semibold text-ink-primary')}>{row.today}</td>
                <td className={cn(TD, 'text-right font-semibold', toneClass(row.tone))}>{row.change}</td>
                <td className={cn(TD, 'text-xs text-ink-secondary')}>{row.downstreamImpact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showDisclosure ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]"
        >
          Show all {rows.length} rows
        </button>
      ) : null}
    </GmCard>
  );
}
